import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useShiftStore } from '@/stores/shift';
import { useActivePatients } from '@/stores/patients';
import { addPatient } from '@/services/firestore/patients';
import { recognizeRamqFromFile } from '@/services/api/ocr-ramq';
import { validateRamqFormat, normalizeRamq, parseRamq } from '@/lib/ramq';
import { PillButton, PillInput } from '@/components/FormField';
import { DateField } from '@/components/DateField';
import { BackHeader } from '@/components/BackHeader';
import { IconCamera } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';

const SEX_OPTIONS = [
  { value: 'H', label: 'Homme' },
  { value: 'F', label: 'Femme' },
];

export function NewPatientPage() {
  const profile = useAuthStore((s) => s.profile);
  const startShift = useShiftStore((s) => s.startNewShift);
  const verifyShift = useShiftStore((s) => s.verifyCurrentShift);
  const currentShift = useShiftStore((s) => s.currentShift);
  const addActiveLocal = useActivePatients((s) => s.addLocal);
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [ramq, setRamq] = useState('');
  const [ramqExp, setRamqExp] = useState('');
  const [sex, setSex] = useState('H');
  const [isNew, setIsNew] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'ocr' | 'save' | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!profile) return;
    void verifyShift(profile.uid);
  }, [profile, verifyShift]);

  // Auto-prefill from the RAMQ when it parses cleanly. Only fills empty
  // fields so we never clobber what the user already typed or what OCR
  // returned alongside the RAMQ.
  useEffect(() => {
    const info = parseRamq(ramq);
    if (!info) return;
    setFirstName((v) => v || info.firstNameInitial);
    setLastName((v) => v || info.lastNamePrefix);
    if (info.dob) setDob((v) => v || info.dob!);
    if (info.sex) setSex((v) => (v && v !== 'H' ? v : info.sex!));
  }, [ramq]);

  async function onOcrFile(file: File) {
    setError(null);
    setBusy('ocr');
    try {
      const res = await recognizeRamqFromFile(file);
      if (res.ramq) setRamq(res.ramq);
      if (res.firstName) setFirstName(res.firstName);
      if (res.lastName) setLastName(res.lastName);
      if (!res.ramq) setError('Carte non reconnue, saisissez manuellement.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!profile) return;
    setError(null);
    const ramqV = validateRamqFormat(ramq);
    if (!ramqV.isValid) {
      setError(ramqV.errorMessage);
      return;
    }
    const hasRamq = !!normalizeRamq(ramq);
    const hasNames = !!firstName.trim() && !!lastName.trim();
    if (!hasRamq && !hasNames) {
      setError('Fournissez une RAMQ valide ou nom + prénom.');
      return;
    }

    setBusy('save');
    try {
      if (!currentShift.dateStarted) {
        await startShift({
          uid: profile.uid,
          mainPlace: profile.mainPlace || 'chum',
          mdType: profile.mdType || 'cfpc',
          facturationType: profile.billingType || 'mixte',
        });
      }
      const patient = await addPatient({
        uid: profile.uid,
        firstName,
        lastName,
        dob,
        ramq,
        ramqExp,
        sex,
        isNew,
      });
      addActiveLocal(patient);
      navigate(`/patient/${patient.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="p-4 flex flex-col gap-3 pb-24">
      <BackHeader title="Nouveau patient" />

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onOcrFile(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy != null}
        className="rounded-pill bg-white/10 border border-white/20 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {busy === 'ocr' ? (
          <Spinner size={18} />
        ) : (
          <>
            <IconCamera size={18} />
            <span>Numériser la RAMQ</span>
          </>
        )}
      </button>

      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <PillInput
          placeholder="Prénom"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <PillInput
          placeholder="Nom de famille"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <div className="flex flex-col gap-2">
          <p className="text-xs text-white/60 px-2">Date de naissance</p>
          <DateField
            value={dob}
            onChange={setDob}
            placeholder="AAAA-MM-JJ"
          />
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-xs text-white/60 px-2">Sexe</p>
          <div className="flex bg-brand-dark/30 rounded-pill p-1">
            {SEX_OPTIONS.map((o) => {
              const active = sex === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setSex(o.value)}
                  className={`flex-1 py-2 rounded-pill text-sm font-semibold transition-colors ${
                    active ? 'bg-brand-orange text-white' : 'text-white/70'
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
        <PillInput
          placeholder="RAMQ (AAAA99999999)"
          value={ramq}
          autoCapitalize="characters"
          onChange={(e) => setRamq(e.target.value.toUpperCase())}
          maxLength={14}
        />
        <DateField
          variant="month"
          value={ramqExp.slice(0, 7)}
          onChange={setRamqExp}
          placeholder="Expiration RAMQ"
        />
        <label className="text-sm flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={isNew}
            onChange={(e) => setIsNew(e.target.checked)}
          />
          Nouveau patient (visite initiale)
        </label>

        {error && <p className="text-sm text-red-300">{error}</p>}

        <PillButton type="submit" disabled={busy != null}>
          {busy === 'save' ? (
            <span className="inline-flex items-center justify-center">
              <Spinner />
            </span>
          ) : (
            'Ajouter le patient'
          )}
        </PillButton>
      </form>
    </div>
  );
}
